import { Group, Layer, Rect, Stage, Image } from 'react-konva';
import React, { memo, useState } from 'react';
import { IStyle } from 'Styles';
import DisableImage from '../plate/DisableImage';
import { CanvasTooltipProps } from 'components/canvas/tooltip';
import { useRMDispatch } from 'redux/hooks';
import { toggleWellIndependentZoom } from 'redux/reducers/wellReducer';
import { RMSvgIconImage } from 'components/customComponents/CommonComponents/RMSvgIcon';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';

interface Props {
  wellNumber: string;
  wellInfo: string;
  wellScores: any;
  disableImagingIcon?: React.ReactNode;
  isImagingDisabled?: boolean;
  canvasWidth: number;
  selectedWellsLength?: number;
  wellKey?: string;
  isIndependentZoom?: boolean;
}
const iconAndScoreSize = 24; // there is some issue with compare well render component. that's just workaround
const syncZoomIcon = RMSvgIconImage({ name: 'SyncZoom', fill: 'white' });
const unSyncZoomIcon = RMSvgIconImage({ name: 'UnSyncZoom', fill: 'white' });
const WellCanvasInfoHeader = (props: Props) => {
  const {
    canvasWidth,
    wellInfo,
    wellNumber,
    wellScores,
    disableImagingIcon,
    isImagingDisabled,
    selectedWellsLength,
    wellKey,
    isIndependentZoom,
  } = props;
  const defaultTooltipState = {
    windowPointerPositionX: 0,
    windowPointerPositionY: 0,
    scale: 1,
    stage: null,
    toolTipText: null,
  };
  const [tooltipData, setTooltipData] = useState<CanvasTooltipProps>(defaultTooltipState);
  const dispatch = useRMDispatch();
  const isCompareModeWithMultipleWells =
    (window?.location?.pathname.includes('/compare') ||
      window?.location?.pathname.includes('/well')) &&
    selectedWellsLength > 1;
  const AllowDisputedScoreDrawing = useSystemProperty(
    PropertyConstants.AllowDisputedScoreDrawing,
    false,
  );
  const isAnyScoreDisputed = wellScores?.props?.scoresOfWellDrops?.some(
    (el) => el.isDisputed === true,
  );
  const scoresCount =
    (wellScores?.props?.scoresOfWellDrops?.length || 0) +
    (isAnyScoreDisputed && AllowDisputedScoreDrawing ? 1 : 0);
  const width = canvasWidth + (wellInfo ? canvasWidth * 0.01 : 0); //adjust width
  const scoresXWidthFactor = isCompareModeWithMultipleWells ? 0.49 : 0.5;
  const baseWidth = wellInfo ? width * scoresXWidthFactor : width * 0.79;

  const compareModeOffset = isCompareModeWithMultipleWells ? iconAndScoreSize : 0;
  const imagingOffset = disableImagingIcon || isImagingDisabled ? iconAndScoreSize : 0;

  const scoresX =
    baseWidth - scoresCount * iconAndScoreSize - compareModeOffset - imagingOffset;

  function onMouseMove(evt) {
    evt.evt.preventDefault();
    const { clientX, clientY } = evt.evt;
    if (evt.target.attrs.tooltipInfo) {
      setTooltipData({
        windowPointerPositionX: clientX,
        windowPointerPositionY: clientY,
        scale: 1,
        stage: null,
        toolTipText: evt.target.attrs.tooltipInfo,
      });
    } else {
      if (tooltipData.toolTipText !== null) {
        setTooltipData(defaultTooltipState);
      }
    }
  }

  const getTooltipPosition = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let adjustedX = tooltipData.windowPointerPositionX - 35;
    const adjustedY = tooltipData.windowPointerPositionY + 20;

    // Adjust if tooltip is going out of the right viewport
    if (adjustedX + 150 > viewportWidth) {
      adjustedX = viewportWidth - 150 - 10;
    }

    // Convert to percentage
    const leftPercent = (adjustedX / viewportWidth) * 100;
    const topPercent = (adjustedY / viewportHeight) * 100;

    return { top: `${topPercent}%`, left: `${leftPercent}%` };
  };

  return (
    <div id="wellcanvasinfoheader-root" style={{ ...styles.root, width }}>
      <div
        id="wellcanvasinfoheader-info-root"
        style={{ ...styles.infoRoot, width: wellInfo ? '51%' : '20%' }}
      >
        <div id="wellcanvasinfoheader-well-number" style={styles.wellNumber}>
          {wellNumber}
        </div>
        <div id="wellcanvasinfoheader-separator" style={styles.separator}></div>
        <div
          id="wellcanvasinfoheader-well-info-text"
          style={styles.wellInfoText}
          title={wellInfo}
        >
          {wellInfo}
        </div>
      </div>
      <div
        id="wellcanvasinfoheader-canvas-root"
        style={{ ...styles.canvasRoot, width: wellInfo ? '51%' : '80%' }}
      >
        <Stage
          id={'wellcanvasinfoheader-stageInfo'}
          width={wellInfo ? width / 2 : width * 0.79}
          height={24}
          draggable={false}
          x={0} // workaround for compare route issue with canvas width and x coords
          onMouseMove={onMouseMove}
          onMouseOut={() => setTooltipData(defaultTooltipState)}
        >
          {isCompareModeWithMultipleWells && wellKey && (
            <Layer
              id="wellcanvasinfoheader-canvas-stage-layer"
              x={width * scoresXWidthFactor - iconAndScoreSize}
              onClick={() => {
                dispatch(toggleWellIndependentZoom(wellKey));
              }}
            >
              <Group id="wellcanvasinfoheader-canvas-stage-layer-group">
                <Rect
                  id="wellcanvasinfoheader-canvas-stage-layer-rect"
                  width={iconAndScoreSize}
                  height={iconAndScoreSize}
                  fill="gray"
                  cornerRadius={5}
                  tooltipInfo={isIndependentZoom ? 'Sync Zoom' : 'Unsync Zoom'}
                />
                <Image
                  id="wellcanvasinfoheader-canvas-stage-layer-image"
                  image={isIndependentZoom ? unSyncZoomIcon : syncZoomIcon}
                  width={iconAndScoreSize}
                  height={iconAndScoreSize}
                  tooltipInfo={isIndependentZoom ? 'Sync Zoom' : 'Unsync Zoom'}
                />
              </Group>
            </Layer>
          )}

          <Layer id="wellcanvasinfoheader-canvas-stage-layer-score" x={scoresX}>
            {wellScores}
          </Layer>
          {/**
           * // incorporate disableimage coords if icon exists
           */}
          <Layer
            id="wellcanvasinfoheader-canvas-stage-layer-compare-mode-imaging"
            x={
              width * scoresXWidthFactor -
              (isCompareModeWithMultipleWells ? 2 * iconAndScoreSize : iconAndScoreSize)
            }
          >
            {disableImagingIcon && disableImagingIcon}
            {isImagingDisabled && <DisableImage x={0} y={0} width={24} height={24} />}
          </Layer>
        </Stage>
      </div>
      <div
        id="wellcanvasinfoheader-tooltip-root"
        style={{
          ...getTooltipPosition(),
          backgroundColor: '#F5F5F5',
          border: '1px solid black',
          zIndex: '1',
          padding: '0px',
          position: 'absolute',
        }}
      >
        {tooltipData?.toolTipText?.split('\n').map((line, index) => (
          <React.Fragment key={index}>
            {line}
            <br />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const styles: IStyle = {
  root: {
    display: 'flex',
    border: '2px solid lightGray',
    justifyContent: 'space-between',
    backgroundColor: 'white',
  },
  infoRoot: {
    display: 'flex',
    flexDirection: 'row',
  },
  canvasRoot: {
    height: '24px',
    maxHeight: '24px',
  },
  separator: {
    borderLeft: '2px solid gray',
    marginRight: '1%',
  },
  wellNumber: {
    fontWeight: 'bold',
    paddingLeft: '1%',
    paddingRight: '1%',
    whiteSpace: 'nowrap',
  },
  wellInfoText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

export default memo(WellCanvasInfoHeader);
